import { useEffect, useState } from 'react';
import { mayLeaveTheLetter, useAssistantRail } from '../contexts/AssistantRailContext';
import { useNavigate } from 'react-router-dom';
import { api, type SubjectType } from '../lib/api';

/**
 * UI3 - switching between property threads and general chats WITHOUT leaving the record.
 *
 * His words: *"there's the threads that lets me switch to other property threads/general
 * threads."*
 *
 * ⭐⭐⭐ "GENERAL" IS THE APP'S OWN WORD, NOT A GUESS. `AssistantPanel` already labels a page with
 * no record `General`, and the rail's two tabs are Thread (shared with Robert, bound to a record)
 * and Assistant (private, bound to nothing). So "other property threads" is the Thread tab's
 * history and "general threads" is the Assistant tab's - and **measured, those two histories live
 * on two SEPARATE pages and neither page shows the other**: `/threads` reads `property_threads`,
 * `/ai` reads `ai_app_chats` + `ai_deal_chats`. ⛔ `AssistantPanel`'s own comment says `/ai`
 * "already lists BOTH threads and app chats" - it does not; measured, `getConversations` selects
 * only the two `ai_*_chats` tables, and its "threads" means Deal Advisor chats.
 *
 * ⛔⛔ AND IT IS A PICKER, NOT A SECOND HISTORY BROWSER. The objection that stood in the code -
 * *"a second history browser inside a narrow column would be one too many"* - is right, and this
 * is built around it rather than through it: no search, no previews, no message view, a hard
 * `MAX_ROWS` cap per section, and a door to the real page underneath. What it adds is the SWITCH,
 * which is the part he asked for and the part `/threads` and `/ai` can only give by navigating
 * away from the record he is standing on.
 *
 * ⛔ A PROPERTY THREAD NAVIGATES; A GENERAL CHAT LOADS IN PLACE. That asymmetry is load-bearing.
 * `PageSubjectContext` exists so the rail can never disagree with the record on screen, so a
 * thread belonging to another property cannot be shown beside THIS property's page - picking one
 * goes to that property, and the Thread tab then shows its thread because the page published it.
 * A general chat is bound to no record at all, so it can open right here, which is the whole
 * point: today the only door to it is `History`, and that door NAVIGATES AWAY.
 */

/** ⛔ A cap, not a page. This is a switcher; `/threads` and `/ai` are where you browse. */
const MAX_ROWS = 8;

export interface RailSwitcherProps {
  /** The record the page is showing, so its own thread can be marked rather than offered. */
  currentSubjectId: string | null;
  currentSubjectType: SubjectType | null;
  /** Load this general chat into the Assistant tab, in place. */
  onOpenChat: (chatId: string) => void;
  onClose: () => void;
}

interface ThreadRow {
  id: string;
  subjectId: string | null;
  subjectType: SubjectType | null;
  label: string;
  city: string | null;
  href: string | null;
  unread: number;
  muted: boolean;
}

interface ChatRow {
  id: string;
  label: string;
  updatedAt: string;
}

/**
 * What to call a general chat in one line.
 *
 * ⭐⭐ MEASURED, and it is why this is not just `chat.title`: of his 10 app chats **8 carry a
 * title, 2 carry NEITHER a title nor a page_context**. The API's fallback is the constant string
 * `App Assistant Chat`, so those two would render as the same row twice - two entries that look
 * identical and cannot be told apart, which is a picker that cannot be used. The date is what
 * actually distinguishes them.
 */
export function chatLabel(c: { title?: string | null; page_context?: string | null; updated_at: string }): string {
  const title = (c.title || '').trim();
  // The API substitutes this constant for a null title, so it is not evidence of a real name.
  if (title && title !== 'App Assistant Chat') return title;
  // `page_context` is the fact block; its FIRST line is "Property: 1200 W 2nd St".
  const ctx = (c.page_context || '').split('\n')[0].trim();
  if (ctx) return ctx;
  return `Chat from ${new Date(c.updated_at).toLocaleDateString()}`;
}

export default function RailSwitcher({
  currentSubjectId,
  currentSubjectType,
  onOpenChat,
  onClose,
}: RailSwitcherProps) {
  const navigate = useNavigate();
  /**
   * ⭐ The 112th's review (I2): these three leave the page - and beside a letter still being written (the chat now sits
   *   beside it), the letter goes with it. Asked first, by the one rule every rail link shares.
   */
  const rail = useAssistantRail();
  const [threads, setThreads] = useState<ThreadRow[] | null>(null);
  const [chats, setChats] = useState<ChatRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      // ⭐ Both fetched together and settled independently: one list failing must not blank the
      // other. A switcher that shows nothing because an unrelated endpoint is down reads as "you
      // have no conversations", which is the wrong kind of wrong.
      const [t, c] = await Promise.allSettled([api.getThreadInbox(), api.aiConversations('app')]);
      if (cancelled) return;

      if (t.status === 'fulfilled') {
        setThreads(
          t.value.threads.map((row) => ({
            id: row.id,
            subjectId: row.subject?.id ?? null,
            subjectType: row.subject?.type ?? null,
            label: row.subject?.label || row.title || 'Untitled',
            city: row.subject?.city ?? null,
            href: row.subject?.href ?? null,
            unread: row.unread,
            muted: row.muted,
          })),
        );
      } else {
        setThreads([]);
      }

      if (c.status === 'fulfilled') {
        setChats(
          (c.value.conversations || []).map((row: { id: string; title?: string | null; page_context?: string | null; updated_at: string }) => ({
            id: row.id,
            label: chatLabel(row),
            updatedAt: row.updated_at,
          })),
        );
      } else {
        setChats([]);
      }

      if (t.status === 'rejected' && c.status === 'rejected') {
        setError('Could not load your conversations.');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loading = threads === null || chats === null;
  const shownThreads = (threads ?? []).slice(0, MAX_ROWS);
  const shownChats = (chats ?? []).slice(0, MAX_ROWS);

  const rowClass =
    'flex w-full items-center gap-2 px-[18px] py-2.5 text-left text-[13px] hover:bg-gray-50';

  return (
    <div className="flex flex-col overflow-y-auto border-b border-gray-200 bg-white" data-testid="rail-switcher">
      <p className="px-[18px] pt-3 pb-1 text-[10px] font-bold uppercase tracking-[.12em] text-gray-500">
        Property threads
      </p>
      {loading && <p className="px-[18px] py-2 text-[12.5px] text-gray-500">Loading…</p>}
      {!loading && shownThreads.length === 0 && (
        <p className="px-[18px] py-2 text-[12.5px] text-gray-500">
          No property threads yet.
        </p>
      )}
      {shownThreads.map((t) => {
        // ⭐ The record on screen is MARKED, never offered as somewhere to go. Navigating to the
        // page you are already on is a control that does nothing, and reads as broken.
        // ⛔ TYPE AND ID, never the id alone. `SubjectType` is three tables, so "the same id"
        // is only "the same record" once the table agrees too - the identity bug the property
        // work has already paid for twice (`properties` vs `master_properties`).
        const isCurrent =
          !!currentSubjectId && t.subjectId === currentSubjectId && t.subjectType === currentSubjectType;
        if (isCurrent) {
          return (
            <div key={t.id} className={`${rowClass} cursor-default bg-gray-50`}>
              <span className="min-w-0 flex-1 truncate font-semibold text-gray-900">{t.label}</span>
              <span className="shrink-0 text-[10px] font-semibold uppercase tracking-wider text-gray-500">
                On screen
              </span>
            </div>
          );
        }
        return (
          <button
            key={t.id}
            type="button"
            className={rowClass}
            // ⛔ No href means the subject could not be resolved (a deleted record). Going nowhere
            // is better than a blank page, which is what a wrong route renders here with no error.
            disabled={!t.href}
            onClick={() => { if (t.href && mayLeaveTheLetter(rail)) { navigate(t.href); onClose(); } }}
          >
            <span className="min-w-0 flex-1 truncate text-gray-800">
              {t.label}
              {t.city && <span className="text-gray-500"> · {t.city}</span>}
            </span>
            {t.unread > 0 && !t.muted && (
              <span className="shrink-0 rounded-full bg-primary-500 px-1.5 py-0.5 text-[10px] font-semibold text-white">
                {t.unread}
              </span>
            )}
          </button>
        );
      })}

      <p className="px-[18px] pt-3 pb-1 text-[10px] font-bold uppercase tracking-[.12em] text-gray-500">
        General chats
      </p>
      {!loading && shownChats.length === 0 && (
        <p className="px-[18px] py-2 text-[12.5px] text-gray-500">No general chats yet.</p>
      )}
      {shownChats.map((c) => (
        <button
          key={c.id}
          type="button"
          className={rowClass}
          onClick={() => { onOpenChat(c.id); onClose(); }}
        >
          <span className="min-w-0 flex-1 truncate text-gray-800">{c.label}</span>
        </button>
      ))}

      {error && (
        <p className="px-[18px] py-2 text-[12.5px] text-primary-500">{error}</p>
      )}

      {/* ⭐ The door to the real pages, so the cap above is never a dead end - and so this stays
          a picker rather than growing into the second history browser the code warned about. */}
      <div className="flex items-center gap-4 border-t border-gray-100 px-[18px] py-2.5">
        <button
          type="button"
          onClick={() => { if (mayLeaveTheLetter(rail)) { navigate('/threads'); onClose(); } }}
          className="text-[12px] font-semibold text-primary-500 underline-offset-[3px] hover:underline"
        >
          All threads
        </button>
        <button
          type="button"
          onClick={() => { if (mayLeaveTheLetter(rail)) { navigate('/ai'); onClose(); } }}
          className="text-[12px] font-semibold text-primary-500 underline-offset-[3px] hover:underline"
        >
          All chats
        </button>
      </div>
    </div>
  );
}
